import React, { useEffect, useRef, useState } from "react";
import { Header3, muiInputFormsCSS, SolidButton, styled, Body1, StyledFieldLabel } from "@iventis/styles";
import { useResizeObserver } from "@iventis/utilities";
import type { Feature, Polygon } from "geojson";
import { GeoJSONFeature } from "@iventis/mapbox-gl";
import bboxPolygon from "@turf/bbox-polygon";
import bbox from "@turf/bbox";
import { CursorTooltip } from "@iventis/components";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { CadUploadConfigUnit } from "@iventis/domain-model/model/cadUploadConfigUnit";
import { Asset } from "@iventis/domain-model/model/asset";
import { MapboxCadGeoReferencer } from "../../mapbox/mapbox-cad-editor";
import { useSubjectContext } from "../../state/subjects-context";
import { LayerListComponent } from "../layer-filtering/filtered-layer-list";
import { MapSearch } from "../map-search/map-search";
import { BackgroundSelectorComponent } from "../map-options/background-selector";
import { OSKey } from "../../api-keys";
import { MapZoomComponent } from "../map-options/map-zoom";
import { UnitSelectorComponent } from "../map-options/unit-selector";
import { RotationCad } from "../map-options/rotation-cad";
import { ClippingModeControls } from "./clipping-mode-controls";
import { GeoReferencerCadMode } from "../../types/georeferencer-cad-mode";
import { CadPositionLockButton } from "../map-options/cad-position-lock-button";
import { ModeStepperComponent } from "../mode-stepper";
import "@iventis/mapbox-gl/dist/mapbox-gl.css";
import { CadControlPoint } from "../../mapbox/types";
import { PerspectiveTransform } from "../../geo-helpers/perspective-transform";
import { InvalidGeoJsonError } from "../../geo-helpers/invalid-geo-json-error";
import { CadConfig } from "../../types/cad-config";
import { unProjectFromEpsg4326 } from "../../geo-helpers/feature-collection";
import { useCadApiContext } from "../../context/cad-api-context";

const nonGeoreferencedModes = [
    GeoReferencerCadMode.WaitingForFile,
    GeoReferencerCadMode.ClippingAndLayerExclusions,
    GeoReferencerCadMode.LocationPicker,
    GeoReferencerCadMode.Positioning,
    GeoReferencerCadMode.LayerExclusions,
    GeoReferencerCadMode.Perimeter,
];

export function CadEditor({
    onFinish,
    getGoogleMapBackgroundApiKey,
    getMapBackgrounds,
    getSitemapVersionLevelAsset,
}: {
    onFinish: (cadConfig: CadConfig) => void;
    getGoogleMapBackgroundApiKey: () => Promise<{ session: string; apiKey: string }>;
    getMapBackgrounds?: () => Promise<Asset[]>;
    getSitemapVersionLevelAsset?: (levelId: string) => Promise<Asset>;
}) {
    const subjects = useSubjectContext();
    const cadApiContext = useCadApiContext();
    const [mode, setMode] = useState<GeoReferencerCadMode>(subjects.mode$.value);
    const [controlPoints, setControlPoints] = useState<CadControlPoint[]>(subjects.controlPoints$.value);
    const [unitsError, setUnitsError] = useState<string | null>(subjects.unitsError$.value);
    const [perimeter, setPerimeter] = useState<Polygon>();
    const [clippedArea, setClippedArea] = useState<Polygon>();
    const clippedAreaRef = useRef<Polygon>();
    const [projectPercentage, setProjectPercentage] = useState<number>(0);

    // Keep clippedAreaRef in sync with clippedArea
    useEffect(() => {
        clippedAreaRef.current = clippedArea;
    }, [clippedArea]);

    const [bounding, setBounding] = useState<Polygon>();
    const [filteredLayers, setFilteredLayers] = useState<string[]>([]);
    const [projecting, setProjecting] = useState<boolean>(false);
    const cadMapElementRef = useRef<HTMLDivElement>(null);
    const backgroundMapElementRef = useRef<HTMLDivElement>(null);
    const mapboxCadGeoReferencerRef = useRef<MapboxCadGeoReferencer>();
    const pivotPointRef = useRef<[number, number] | null>(null);
    const [showProjectionButton, setShowProjectionButton] = useState<boolean>(false);

    const resizeMap = () => {
        mapboxCadGeoReferencerRef.current?.resizeMap();
    };

    const getCadConfig = () => {
        const sourcePoints = controlPoints.map((coord) => [coord.cad[0], coord.cad[1]]);
        const destPoints = controlPoints.map((coord) => [coord.realWorld[0], coord.realWorld[1]]);
        // Compute the transform matrix
        const transformer = new PerspectiveTransform(sourcePoints, destPoints);
        const boundingBoxCoords = bounding.coordinates[0].map((coord) => {
            const [x, y] = transformer.map(coord[0], coord[1]);
            return [x, y];
        });
        const boundingBox = { ...bounding, coordinates: [boundingBoxCoords] };

        const config: CadConfig = {
            crop: clippedArea.coordinates[0].map((coord) => [coord[0], coord[1]]),
            excludedLayerIds: filteredLayers,
            sourceEpsgCode: null, // Non-georeferenced cads do not have a source EPSG code
            controlPoints: controlPoints.map((point) => ({
                source: point.cad,
                target: point.realWorld,
            })),
            unitOfMeasure: subjects.unitsSelected$.value,
            bounding: boundingBox,
            perimeter,
        };
        return config;
    };

    useResizeObserver(cadMapElementRef, resizeMap);
    useResizeObserver(backgroundMapElementRef, resizeMap);

    const nextStage = () => {
        const currentIndex = nonGeoreferencedModes.indexOf(mode);
        if (currentIndex === nonGeoreferencedModes.length - 1) {
            onFinish(getCadConfig());
            return;
        }

        const nextMode = nonGeoreferencedModes[currentIndex + 1];

        switch (mode) {
            case GeoReferencerCadMode.ClippingAndLayerExclusions:
                mapboxCadGeoReferencerRef.current?.removeCadMapDrawings();
                break;
            case GeoReferencerCadMode.Positioning:
                mapboxCadGeoReferencerRef.current?.hideControlPoints();
                mapboxCadGeoReferencerRef.current?.removeSitemapVersionLevel();
                break;
            default:
                break;
        }

        // pick the next stage based on the current mode using the enum order
        switch (nextMode) {
            case GeoReferencerCadMode.ClippingAndLayerExclusions:
                mapboxCadGeoReferencerRef.current.project(null, 0, true, filteredLayers, [0, 0], null);
                mapboxCadGeoReferencerRef.current?.enableCadMapMovement();
                subjects.mode$.next(GeoReferencerCadMode.ClippingAndLayerExclusions);
                break;
            case GeoReferencerCadMode.LocationPicker:
                mapboxCadGeoReferencerRef.current?.hideCad();
                mapboxCadGeoReferencerRef.current?.fitBackgroundToBounds([-130, 24, -65, 50]);
                subjects.mode$.next(GeoReferencerCadMode.LocationPicker);
                break;
            case GeoReferencerCadMode.Positioning:
                {
                    mapboxCadGeoReferencerRef.current?.showCad();
                    mapboxCadGeoReferencerRef.current?.showControlPoints();
                    subjects.allowCadMovePosition$.next(false);
                    const backgroundCenter = mapboxCadGeoReferencerRef.current?.getBackgroundMapCenter();
                    try {
                        mapboxCadGeoReferencerRef.current.project(
                            subjects.unitsSelected$.value,
                            subjects.cadRotation$.value,
                            true,
                            filteredLayers,
                            [backgroundCenter.lng, backgroundCenter.lat],
                            clippedArea.coordinates[0].map((coord) => [coord[0], coord[1]])
                        );
                    } catch (error) {
                        if (error instanceof InvalidGeoJsonError) {
                            subjects.unitsError$.next(error.message);
                        } else {
                            throw error;
                        }
                    }
                    subjects.mode$.next(GeoReferencerCadMode.Positioning);
                }
                break;
            case GeoReferencerCadMode.LayerExclusions:
                subjects.allowCadMovePosition$.next(true);
                if (subjects.unitsError$.value === null) {
                    subjects.mode$.next(GeoReferencerCadMode.LayerExclusions);
                }
                break;
            case GeoReferencerCadMode.Perimeter:
                subjects.mode$.next(GeoReferencerCadMode.Perimeter);
                break;
            default:
                // eslint-disable-next-line no-console
                console.warn(`Unexpected mode: ${GeoReferencerCadMode}`);
                break;
        }
    };

    const getTooltipText = () => {
        switch (mode) {
            case GeoReferencerCadMode.ClippingAndLayerExclusions:
                if (!clippedArea) {
                    return "Draw clipped area to proceed";
                }
                break;
            case GeoReferencerCadMode.Positioning:
                if (unitsError) {
                    return unitsError;
                }
                break;
            case GeoReferencerCadMode.Perimeter:
                if (!perimeter) {
                    return "Draw perimeter to proceed";
                }
                return "Finish";
            default:
                break;
        }
        return "Next Stage";
    };

    const isNextStepDisabled = (): boolean => {
        switch (mode) {
            case GeoReferencerCadMode.ClippingAndLayerExclusions:
                return !clippedArea;
            case GeoReferencerCadMode.Positioning:
                return !!unitsError;
            case GeoReferencerCadMode.Perimeter:
                return !perimeter;
            default:
                return false;
        }
    };

    const [configLoaded, setConfigLoaded] = useState(false);
    const [featuresRead, setFeaturesRead] = useState<number>(0);
    const [layerNames, setLayerNames] = useState<string[]>([]);

    const onReProject = () => {
        clearRotationPoints();
        setShowProjectionButton(false);
        mapboxCadGeoReferencerRef.current.project(
            subjects.unitsSelected$.value,
            subjects.cadRotation$.value,
            false,
            filteredLayers,
            mapboxCadGeoReferencerRef.current.getCadRealWorldCenter(),
            clippedAreaRef.current?.coordinates[0].map((coord) => [coord[0], coord[1]])
        );
    };

    // Poll config state until idle, then allow main effect to run
    useEffect(() => {
        let polling = true;
        let intervalId: NodeJS.Timeout;
        async function pollConfig() {
            try {
                const config = await cadApiContext.getConfig();
                setFeaturesRead(config.featuresRead ?? 0); // Update features read count
                if (config.state === "idle" || config.state === "projecting") {
                    const geoJsonlayerNames = await cadApiContext.getGeoJsonLayers(); // Adjust URL as needed
                    setLayerNames(geoJsonlayerNames);
                    setConfigLoaded(true);
                    polling = false;
                    clearInterval(intervalId);
                }
            } catch (e) {
                // Optionally handle error
            }
        }
        pollConfig();
        intervalId = setInterval(() => {
            if (polling) pollConfig();
        }, 2000);
        return () => {
            polling = false;
            if (intervalId != null) clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        if (!configLoaded) return;
        mapboxCadGeoReferencerRef.current = new MapboxCadGeoReferencer(
            () => {
                nextStage();
            },
            cadMapElementRef.current,
            backgroundMapElementRef.current,
            { getGoogleMapBackgroundApiKey, osMapsApiKey: OSKey },
            subjects,
            cadApiContext,
            getSitemapVersionLevelAsset
        );

        const modeSubscription = subjects.mode$.subscribe((newMode) => {
            setMode(newMode);
        });

        const errorSubscription = subjects.unitsError$.subscribe((errorMessage) => {
            setUnitsError(errorMessage);
        });

        const backgroundSelectionSubscription = subjects.backgroundSelected$.subscribe((background) => {
            if (mapboxCadGeoReferencerRef.current) {
                mapboxCadGeoReferencerRef.current.changeBackground(background);
            }
        });

        const unitsSubscription = subjects.unitsSelected$.subscribe((units) => {
            subjects.unitsError$.next(null); // reset units error
            if (mapboxCadGeoReferencerRef.current && subjects.mode$.value === GeoReferencerCadMode.Positioning) {
                const backgroundCenter = mapboxCadGeoReferencerRef.current?.getBackgroundMapCenter();
                try {
                    setShowProjectionButton(false);
                    mapboxCadGeoReferencerRef.current.project(
                        units,
                        subjects.cadRotation$.value,
                        true,
                        filteredLayers,
                        [backgroundCenter.lng, backgroundCenter.lat],
                        clippedAreaRef.current?.coordinates[0].map((coord) => [coord[0], coord[1]])
                    );
                } catch (error) {
                    if (error instanceof InvalidGeoJsonError) {
                        subjects.unitsError$.next(error.message);
                    } else {
                        throw error;
                    }
                }
            }
        });

        const rotationSubscription = subjects.cadRotation$.subscribe((rotationValue) => {
            if (mapboxCadGeoReferencerRef.current && subjects.mode$.value === GeoReferencerCadMode.Positioning) {
                try {
                    setShowProjectionButton(true);
                    const pivot = pivotPointRef.current;
                    mapboxCadGeoReferencerRef.current.rotateCad(rotationValue, pivot ?? undefined);
                } catch (error) {
                    if (error instanceof InvalidGeoJsonError) {
                        subjects.unitsError$.next(error.message);
                    } else {
                        throw error;
                    }
                }
            }
        });

        const layerIdsSubscription = subjects.filterLayers$.subscribe((layerIds) => {
            mapboxCadGeoReferencerRef.current?.filterCadLayers(layerIds);
            setFilteredLayers(layerIds);
        });

        const cadLockSubscription = subjects.allowCadMovePosition$.subscribe((allowCadMovePosition) => {
            if (mapboxCadGeoReferencerRef.current) {
                if (allowCadMovePosition) {
                    mapboxCadGeoReferencerRef.current.enableCadMapMovement();
                } else {
                    mapboxCadGeoReferencerRef.current.disableCadMapMovement();
                }
            }
        });

        const searchSubscription = subjects.searchLocationSelected$.subscribe((location) => {
            mapboxCadGeoReferencerRef.current?.fitBackgroundToBounds(location);
        });

        if (cadMapElementRef.current == null || backgroundMapElementRef.current == null) {
            throw new Error("Elements for attaching maps are null");
        }

        const controlPointsChangeSubscription = mapboxCadGeoReferencerRef.current.onControlPointsChange.subscribe((controlPoints) => {
            if (subjects.mode$.value === GeoReferencerCadMode.Positioning) {
                if (controlPoints && controlPoints.length > 0) {
                    const corners: CadControlPoint[] = controlPoints.map((corner) => ({
                        cornerPosition: corner.corner,
                        realWorld: corner.realWorld,
                        cad: corner.cad,
                    }));
                    setControlPoints(corners);
                }
            }
        });

        const onCadClickedSubscription = mapboxCadGeoReferencerRef.current.onCadClicked().subscribe((clickedFeatures: GeoJSONFeature[]) => {
            if (subjects.mode$.value === GeoReferencerCadMode.ClippingAndLayerExclusions || subjects.mode$.value === GeoReferencerCadMode.LayerExclusions) {
                const layerIds = clickedFeatures.map((feature) => feature.properties?.eLayer).filter((layerId) => layerId != null);
                subjects.filterLayers$.next([...new Set((subjects.filterLayers$.value ?? []).concat(layerIds))]);
            }
        });

        const cadDrawingSubscription = mapboxCadGeoReferencerRef.current.onCadMapDrawEnd().subscribe((drawnArea: Feature<Polygon>) => {
            const clippedArea4326 = drawnArea.geometry;
            const cadClip: Polygon = {
                type: "Polygon",
                coordinates: [[]],
            };
            cadClip.coordinates[0] = clippedArea4326.coordinates[0].map((coord) => {
                const [x, y] = unProjectFromEpsg4326(
                    coord,
                    (mapboxCadGeoReferencerRef.current.originalCadBounds.maxX + mapboxCadGeoReferencerRef.current.originalCadBounds.minX) / 2,
                    (mapboxCadGeoReferencerRef.current.originalCadBounds.maxY + mapboxCadGeoReferencerRef.current.originalCadBounds.minY) / 2,
                    0,
                    0,
                    mapboxCadGeoReferencerRef.current.units
                );
                return [x, y];
            });
            setBounding(bboxPolygon(bbox(cadClip)).geometry);
            setClippedArea(cadClip);
            clippedAreaRef.current = cadClip; // Ensure ref is set immediately
        });

        const backgroundDrawingSubscription = mapboxCadGeoReferencerRef.current.onBackgroundMapDrawEnd().subscribe((drawnArea: Feature<Polygon>) => {
            setPerimeter(drawnArea.geometry);
        });

        const rightClick = mapboxCadGeoReferencerRef.current.onCadRightClick().subscribe((data) => {
            if (subjects.mode$.value === GeoReferencerCadMode.Positioning) {
                // eslint-disable-next-line no-console
                console.log("Right click on cad map", data); // keep for control points checking
            }
        });

        const projectingSubscription = mapboxCadGeoReferencerRef.current.projecting$.subscribe((projecting) => {
            setProjecting(projecting);
        });

        const projectionPercentageSubscription = mapboxCadGeoReferencerRef.current.projectionPercentage$.subscribe((projectionPercentage) => {
            setProjectPercentage(projectionPercentage);
        });

        const resetSubjectValues = () => {
            subjects.mode$.next(nonGeoreferencedModes[0]);
            subjects.unitsSelected$.next(CadUploadConfigUnit.Metres);
            subjects.filterLayers$.next([]);
        };

        // eslint-disable-next-line consistent-return
        return () => {
            mapboxCadGeoReferencerRef.current?.destroy();
            modeSubscription.unsubscribe();
            errorSubscription.unsubscribe();
            cadDrawingSubscription.unsubscribe();
            unitsSubscription.unsubscribe();
            cadLockSubscription.unsubscribe();
            searchSubscription.unsubscribe();
            onCadClickedSubscription.unsubscribe();
            layerIdsSubscription.unsubscribe();
            backgroundSelectionSubscription.unsubscribe();
            controlPointsChangeSubscription.unsubscribe();
            backgroundDrawingSubscription.unsubscribe();
            rotationSubscription.unsubscribe();
            rightClick.unsubscribe();
            projectingSubscription.unsubscribe();
            projectionPercentageSubscription.unsubscribe();
            resetSubjectValues();
        };
    }, [configLoaded]);

    const [levelOverlayInput, setLevelOverlayInput] = useState("");
    const [levelOverlayInputError, setLevelOverlayInputError] = useState("");

    // UUID validation regex
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    const handleLevelOverlayInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim();
        setLevelOverlayInput(value);
        if (value === "") {
            mapboxCadGeoReferencerRef.current?.removeSitemapVersionLevel();
            setLevelOverlayInputError("");
            return;
        }
        if (uuidRegex.test(value)) {
            mapboxCadGeoReferencerRef.current?.addSitemapVersionLevel(value);
            setLevelOverlayInputError("");
        } else {
            mapboxCadGeoReferencerRef.current?.removeSitemapVersionLevel();
            setLevelOverlayInputError("Invalid UUID");
        }
    };

    const [pivotPoint, setPivotPoint] = useState<[number, number] | null>(null);
    const [placingPivot, setPlacingPivot] = useState(false);

    useEffect(() => {
        pivotPointRef.current = pivotPoint;
    }, [pivotPoint]);

    // Handler for map click to set pivot point
    useEffect(() => {
        if (!placingPivot || !mapboxCadGeoReferencerRef.current) return;

        mapboxCadGeoReferencerRef.current.setCadMapCursor("crosshair");

        const subscription = mapboxCadGeoReferencerRef.current.onCadMapClick().subscribe((coords: [number, number]) => {
            setPivotPoint(coords);
            setPlacingPivot(false);
        });

        // eslint-disable-next-line consistent-return
        return () => {
            subscription.unsubscribe();
            mapboxCadGeoReferencerRef.current?.setCadMapCursor(null);
        };
    }, [placingPivot]);

    // Called from RotationCad
    const drawPivotPoint = () => {
        // Clear existing pivot so re-placement reinitialises listeners
        setPivotPoint(null);
        setPlacingPivot(true);
    };

    const [rotationHandle, setRotationHandle] = useState<[number, number] | null>(null);
    const [placingPivotHandle, setPlacingPivotHandle] = useState(false);

    const clearRotationPoints = () => {
        setPivotPoint(null);
        setRotationHandle(null);
        setPlacingPivot(false);
        setPlacingPivotHandle(false);
        mapboxCadGeoReferencerRef.current?.setPivotAndHandle(null, null);
    };

    const removePivotPoint = () => {
        setPivotPoint(null);
        if (mapboxCadGeoReferencerRef.current) {
            mapboxCadGeoReferencerRef.current.setPivotAndHandle(null, rotationHandle);
        }
    };

    const removeRotationHandle = () => {
        setRotationHandle(null);
        mapboxCadGeoReferencerRef.current?.setPivotAndHandle(pivotPoint, null);
    };

    const cancelPlacePivotPoint = () => {
        setPlacingPivot(false);
    };

    const cancelPlaceRotationHandle = () => {
        setPlacingPivotHandle(false);
    };

    // Handler for map click to set rotation handle
    useEffect(() => {
        if (!placingPivotHandle || !mapboxCadGeoReferencerRef.current) return;

        mapboxCadGeoReferencerRef.current.setCadMapCursor("crosshair");

        const subscription = mapboxCadGeoReferencerRef.current.onCadMapClick().subscribe((coords: [number, number]) => {
            setRotationHandle(coords);
            setPlacingPivotHandle(false);
        });

        // eslint-disable-next-line consistent-return
        return () => {
            subscription.unsubscribe();
            mapboxCadGeoReferencerRef.current?.setCadMapCursor(null);
        };
    }, [placingPivotHandle]);

    const drawRotationHandle = () => {
        // Clear existing handle so re-placement reinitialises listeners
        setRotationHandle(null);
        setPlacingPivotHandle(true);
    };

    useEffect(() => {
        if (mapboxCadGeoReferencerRef.current) {
            mapboxCadGeoReferencerRef.current.setPivotAndHandle(pivotPoint, rotationHandle);
        }
    }, [pivotPoint, rotationHandle]);

    useEffect(() => {
        if (!mapboxCadGeoReferencerRef.current) return undefined;
        if (!pivotPoint || !rotationHandle) return undefined;

        const cleanup = mapboxCadGeoReferencerRef.current.enableHandleDragging(
            (coords) => setRotationHandle(coords),
            pivotPoint,
            rotationHandle,
            (newRotation) => {
                subjects.cadRotation$.next(newRotation);
                setShowProjectionButton(true);
            }
        );
        return () => cleanup?.();
        // Only re-run when the presence of pivot/handle toggles (placement or removal), not while dragging
    }, [pivotPoint !== null, rotationHandle !== null]);

    return (
        <MapOptionsContainer>
            <StepperRow>
                <ModeStepperComponent mode={mode} georeferenced={false} />
            </StepperRow>
            <ContentRow>
                <div className="sidebar" style={{ overflowY: "auto", minHeight: "0" }}>
                    {(mode === GeoReferencerCadMode.LocationPicker ||
                        mode === GeoReferencerCadMode.Positioning ||
                        mode === GeoReferencerCadMode.Perimeter ||
                        mode === GeoReferencerCadMode.LayerExclusions) && (
                        <>
                            <Header3>Background</Header3>
                            <BackgroundSelectorComponent getMapBackgrounds={getMapBackgrounds} />
                        </>
                    )}
                    {mode === GeoReferencerCadMode.Positioning && (
                        <>
                            <Header3>Control Points</Header3>
                        </>
                    )}
                    {mode === GeoReferencerCadMode.Positioning &&
                        controlPoints?.map((controlPoint: CadControlPoint) => (
                            <div key={controlPoint.cornerPosition}>
                                <p>{controlPoint.cornerPosition}</p>
                                <p>Cad: {controlPoint.cad.join(", ")}</p>
                                <p>Real World: {controlPoint.realWorld.join(", ")}</p>
                            </div>
                        ))}
                    <Header3>Cad settings</Header3>
                    {mode === GeoReferencerCadMode.Positioning && <UnitSelectorComponent />}
                    {mode === GeoReferencerCadMode.Positioning && (
                        <RotationCad
                            placeRotationPivotPoint={drawPivotPoint}
                            placeRotationHandle={drawRotationHandle}
                            removePivotPoint={removePivotPoint}
                            removeRotationHandle={removeRotationHandle}
                            pivotPoint={pivotPoint}
                            rotationHandle={rotationHandle}
                            placingPivotPoint={placingPivot}
                            placingRotationHandle={placingPivotHandle}
                            cancelPlacePivotPoint={cancelPlacePivotPoint}
                            cancelPlaceRotationHandle={cancelPlaceRotationHandle}
                        />
                    )}
                    <MapZoomComponent />
                    {mode === GeoReferencerCadMode.Positioning && <CadPositionLockButton />}
                    {mode === GeoReferencerCadMode.Positioning && (
                        <div>
                            <StyledFieldLabel>Show Level Overlay</StyledFieldLabel>
                            <TextField
                                value={levelOverlayInput}
                                onChange={handleLevelOverlayInputChange}
                                placeholder="Paste or enter UUID"
                                error={!!levelOverlayInputError}
                                sx={{ width: "100%" }}
                                size="small"
                            />
                            {levelOverlayInputError && <Body1 style={{ color: "red" }}>{levelOverlayInputError}</Body1>}
                        </div>
                    )}
                    {(mode === GeoReferencerCadMode.ClippingAndLayerExclusions || mode === GeoReferencerCadMode.LayerExclusions) && <LayerListComponent layerNames={layerNames} />}
                </div>
                <div className="map-and-search">
                    {mode === GeoReferencerCadMode.LocationPicker && <MapSearch />}
                    {mode === GeoReferencerCadMode.ClippingAndLayerExclusions && (
                        <ClippingModeControls onDrawClicked={() => mapboxCadGeoReferencerRef.current?.startCadMapDraw()} />
                    )}
                    {mode === GeoReferencerCadMode.Perimeter && <ClippingModeControls onDrawClicked={() => mapboxCadGeoReferencerRef.current?.startBackgroundMapDraw()} />}
                    {mode === GeoReferencerCadMode.WaitingForFile && <p>Reading CAD file... Read {featuresRead} features</p>}
                    <div className="map-container" style={{ display: mode === GeoReferencerCadMode.WaitingForFile ? "none" : "block" }}>
                        {projecting && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: 10,
                                    right: 10,
                                    zIndex: 100,
                                    background: "#fffbe6",
                                    color: "#ad8b00",
                                    border: "1px solid #ffe58f",
                                    borderRadius: 4,
                                    padding: "8px 16px",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                    fontWeight: 500,
                                }}
                            >
                                Projecting CAD... Please wait {(projectPercentage * 100).toFixed(2)}%
                            </div>
                        )}
                        {showProjectionButton && (
                            <Button
                                style={{
                                    position: "absolute",
                                    top: 10,
                                    right: 10,
                                    zIndex: 100,
                                }}
                                variant="contained"
                                onClick={onReProject}
                            >
                                Re-Project
                            </Button>
                        )}

                        <div className="map-element cad-map" ref={cadMapElementRef} />
                        <div
                            className="map-element background-map"
                            style={{ display: mode === GeoReferencerCadMode.ClippingAndLayerExclusions ? "none" : "block" }}
                            ref={backgroundMapElementRef}
                        />
                        <NextStageButton>
                            <CursorTooltip text={getTooltipText()}>
                                <div>
                                    <SolidButton
                                        type="submit"
                                        variant="contained"
                                        style={{ height: "40px", minWidth: "140px", whiteSpace: "nowrap" }}
                                        disabled={isNextStepDisabled()}
                                        onClick={() => nextStage()}
                                    >
                                        {mode === nonGeoreferencedModes[nonGeoreferencedModes.length - 1] ? <span>Finish</span> : <span>Next Stage</span>}
                                    </SolidButton>
                                </div>
                            </CursorTooltip>
                        </NextStageButton>
                    </div>
                </div>
            </ContentRow>
        </MapOptionsContainer>
    );
}

const MapOptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;

    .map-container {
        width: 100%;
        height: 100%;
        flex-grow: 1;
        position: relative;
    }

    .map-element {
        position: absolute;
        width: 100%;
        height: 100%;
    }

    .cad-map {
        z-index: 2;

        .mapboxgl-ctrl-bottom-left .mapboxgl-ctrl-logo {
            display: none !important;
        }

        .mapboxgl-ctrl-bottom-right {
            display: none !important;
        }
    }

    .background-map {
        z-index: 1;

        .mapboxgl-ctrl-bottom-left .mapboxgl-ctrl-logo {
            display: none !important;
        }

        .mapboxgl-ctrl-bottom-right {
            margin-right: 0;
        }
    }

    .map-and-search {
        display: flex;
        flex-direction: column;
        width: 100%;
        flex: 1;
    }

    .sidebar {
        display: flex;
        flex-direction: column;
        gap: 15px;

        box-sizing: border-box;
        z-index: 3;

        width: 300px;
        overflow-y: auto; /* Add scroll bar */

        ${muiInputFormsCSS}
    }
`;

const StepperRow = styled.div`
    width: 100%;
`;

const ContentRow = styled.div`
    display: flex;
    flex: 1;
    flex-direction: row;
    width: 100%;
    min-height: 0; /* Ensure it can shrink properly */
`;

const NextStageButton = styled.div`
    position: absolute;
    bottom: 10px;
    right: 10px;
    z-index: 10;
`;
